import React, { useState, useEffect } from 'react'
import LoginCard from '../components/landingPage/LoginCard'
import { motion } from 'framer-motion'
import { ChevronRight, ArrowUpRight, ChevronDown, Menu } from 'lucide-react'
import { Redirect, Link, useLocation } from 'react-router-dom'
import { Store, Users, BarChart2, Network } from 'lucide-react'
import '../styles/landing.css'
import { Button, Container, Grid, Typography, Box } from '@mui/material'
import { Wand2, Sparkles, Zap } from 'lucide-react'
import Check from '@mui/icons-material/Check'
import LandingPageAnimation from 'components/landingPage/LandingPageMainAnimation'
import GetEarlyAccessButton from 'components/landingPage/GetEarlyAccessButton'
import Footer from '../components/landingPage/Footer'
import LOGO from '../assets/LOGO.png'

const navItemVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: (isInitialLoad) => ({
        opacity: 1,
        y: 0,
        transition: {
            duration: isInitialLoad ? 0.5 : 0, // Only animate on initial load
            delay: isInitialLoad ? 0.2 : 0, // Only delay on initial load
        },
    }),
}

const solutionArray = [
    {
        title: 'AI-Powered Planning',
        description:
            'Streamline your event planning process with intelligent automation and personalized recommendations.',
        icon: Wand2,
    },
    {
        title: 'Vendor Management',
        description:
            'Easily manage and collaborate with vendors in one centralized platform.',
        icon: Users,
    },
    {
        title: 'Smart Automation',
        description:
            'Automate repetitive tasks and focus on creating exceptional experiences.',
        icon: Sparkles,
    },
    {
        title: 'Real-time Analytics',
        description:
            'Make data-driven decisions with comprehensive event analytics and insights.',
        icon: Zap,
    },
]

const LandingPage = () => {
    const [activeDropdown, setActiveDropdown] = useState(null)
    const [showLoginCard, setShowLoginCard] = useState(false)
    const [redirectTo, setRedirectTo] = useState(null)
    const [isInitialLoad, setIsInitialLoad] = useState(true)
    const [billingCycle, setBillingCycle] = useState('monthly')
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
    const location = useLocation()

    const handleNavigation = (path) => {
        setRedirectTo(path)
    }

    useEffect(() => {
        const timer = setTimeout(() => setIsInitialLoad(false), 800)
        return () => clearTimeout(timer)
    }, [])

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768)
            if (window.innerWidth > 768) {
                setMobileMenuOpen(false)
            }
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    useEffect(() => {
        const params = new URLSearchParams(location.search)
        const inviteCompanyId = params.get('invite_company_id')
        const inviteEventId = params.get('event_id')
        const invitedBy = params.get('invited_by')
        
        // UUID validation regex
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
        
        // Clean and validate company ID
        if (inviteCompanyId) {
            const cleanCompanyId = inviteCompanyId.trim()
            if (uuidRegex.test(cleanCompanyId)) {
                localStorage.setItem('inviteCompanyId', cleanCompanyId)
            } else {
                console.warn('Invalid company ID format:', inviteCompanyId)
            }
        }
        
        // Clean and validate event ID
        if (inviteEventId) {
            const cleanEventId = inviteEventId.trim()
            if (uuidRegex.test(cleanEventId)) {
                localStorage.setItem('inviteEventId', cleanEventId)
            } else {
                console.warn('Invalid event ID format:', inviteEventId)
            }
        }

        // Clean and validate invited_by ID
        if (invitedBy) {
            const cleanInvitedBy = invitedBy.trim()
            if (uuidRegex.test(cleanInvitedBy)) {
                localStorage.setItem('invitedBy', cleanInvitedBy)
            } else {
                console.warn('Invalid invited_by ID format:', invitedBy)
            }
        }
    }, [])

    // To make nav bar sticky
    useEffect(() => {
        const handleScroll = () => {
            const nav = document.querySelector('.nav')
            const featuresSection = document.getElementById('features-section')
            const contactSection = document.getElementById('contact-section')

            if (featuresSection && contactSection) {
                const navRect = nav.getBoundingClientRect()
                const featuresSectionRect =
                    featuresSection.getBoundingClientRect()
                const contactSectionRect =
                    contactSection.getBoundingClientRect()

                // Check if navbar overlaps with either white section
                const overlapsFeatures =
                    navRect.bottom > featuresSectionRect.top &&
                    navRect.top < featuresSectionRect.bottom

                const overlapsContact =
                    navRect.bottom > contactSectionRect.top &&
                    navRect.top < contactSectionRect.bottom

                if (overlapsFeatures || overlapsContact) {
                    nav.classList.add('scrolled')
                } else {
                    nav.classList.remove('scrolled')
                }
            }
        }

        window.addEventListener('scroll', handleScroll)
        // Trigger once on mount to set initial state
        handleScroll()

        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    }, [])

    const handleLoginSuccess = (userData) => {
        setShowLoginCard(false)
        alert('Thank you for requesting access for beta!')
    }

    const navItems = [
        {
            label: 'Pricing',
            scrollTo: 'pricing-section',
        },
        {
            label: 'Contact',
            scrollTo: 'contact-section',
        },
    ]

    const solutionsItem = {
        label: 'Solutions',
        scrollTo: 'features-section',
        dropdown: [
            {
                label: 'Marketplace',
                icon: Store,
                path: '/marketplace',
            },
            {
                label: 'Vendor Management',
                icon: Users,
                path: '/myevents',
            },
            {
                label: 'Analytics',
                icon: BarChart2,
                path: '/eventanalytics',
            },
            {
                label: 'Knowledge Graph',
                icon: Network,
                path: '/',
            },
        ],
    }

    const pricingOptions = [
        {
            title: 'Basic',
            monthlyPrice: 0,
            annualPrice: 0,
            description: 'Perfect for small teams and startups',
            features: [
                'Up to 1 team members',
                'Basic analytics',
                'Up to 5 vendors',
            ],
            mostPopular: false,
        },
        {
            title: 'Pro',
            monthlyPrice: 99,
            annualPrice: 1070,
            description: 'Designed for growing businesses',
            features: [
                'Up to 5 team members',
                'Advanced analytics',
                'Knowledge graph',
                'Up to 10 vendors',
                'Custom integrations',
            ],
            mostPopular: true,
        },
        {
            title: 'Enterprise',
            monthlyPrice: 'Custom',
            annualPrice: 'Custom',
            description: 'Tailored solutions for large organizations',
            features: [
                'Unlimited team members',
                'Advanced analytics',
                'Knowledge graph',
                '100+ vendors',
                '24/7 dedicated support',
                'Advanced security',
                'Dedicated account manager',
            ],
            mostPopular: false,
        },
    ]

    // Add redirect handling
    if (redirectTo) {
        return <Redirect to={redirectTo} />
    }

    // Handle hover for dropdowns
    const handleMouseOver = (dropdownId) => {
        setActiveDropdown(dropdownId)
    }

    const handleMouseOut = () => {
        setActiveDropdown(null)
    }

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId)
        if (element) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            })
        }
    }

    return (
        <>
        <div className="landing-page">
            <nav className="nav">
                <div className="nav-container">
                    <Link to="/">
                        <img
                            src={LOGO}
                            alt="Happenings Logo"
                            className="logo"
                        />
                    </Link>

                    {/* Mobile Menu Button - Only show on mobile */}
                    {isMobile && (
                        <motion.button
                            className="mobile-menu-button"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            aria-label="Toggle menu"
                        >
                            <Menu />
                        </motion.button>
                    )}

                    {/* Nav Items - Always visible on desktop, toggleable on mobile */}
                    <motion.div
                        className={`nav-items ${isMobile && mobileMenuOpen ? 'show-mobile' : ''} ${!isMobile ? 'show-desktop' : ''}`}
                    >
                        <motion.div
                            key="solutions"
                            variants={navItemVariants}
                            custom={isInitialLoad}
                            initial="hidden"
                            animate="visible"
                            className="nav-item"
                            onMouseOver={() =>
                                !isMobile && handleMouseOver('solutions')
                            }
                            onMouseOut={() => !isMobile && handleMouseOut()}
                        >
                            <Link
                                to="#"
                                className="nav-button"
                                onClick={(e) => {
                                    e.preventDefault()
                                    scrollToSection('features-section')
                                    if (isMobile) setMobileMenuOpen(false)
                                }}
                            >
                                {solutionsItem.label}
                                <motion.div
                                    initial={{ rotateX: 0 }}
                                    animate={{
                                        rotateX:
                                            activeDropdown === 'solutions'
                                                ? 180
                                                : 0,
                                    }}
                                    transition={{ duration: 0.2 }}
                                    style={{ marginLeft: '0.5rem' }}
                                ></motion.div>
                            </Link>
                        </motion.div>

                        {navItems.map((item, index) => (
                            <motion.div
                                key={index}
                                variants={navItemVariants}
                                custom={isInitialLoad}
                                initial="hidden"
                                animate="visible"
                                className="nav-item"
                                onMouseOver={() =>
                                    !isMobile && handleMouseOver(index)
                                }
                                onMouseOut={() => !isMobile && handleMouseOut()}
                            >
                                <Link
                                    to={item.scrollTo ? '#' : item.path}
                                    className="nav-button"
                                    onClick={(e) => {
                                        if (item.scrollTo) {
                                            e.preventDefault()
                                            scrollToSection(item.scrollTo)
                                        }
                                        if (isMobile) setMobileMenuOpen(false)
                                    }}
                                >
                                    {item.label}
                                </Link>
                            </motion.div>
                        ))}

                        <GetEarlyAccessButton
                            setShowLoginCard={setShowLoginCard}
                        />
                    </motion.div>
                </div>
            </nav>

            <div className="main-content">
                {/* HERO SECTION - FIXED: Only this section uses Grid */}
                <Box
                    sx={{
                        minHeight: { xs: '100dvh', md: '100vh' },
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        py: { xs: 7, md: 12 },
                        px: { xs: 4, md: 10 },
                        boxSizing: 'border-box',
                    }}
                >
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="center"
                        spacing={{ xs: 4, md: 6 }}
                        sx={{ maxWidth: { xs: '100%', md: '1600px' }, mx: 'auto', width: '100%' }}
                    >
                        {/* Left: Title, Description, CTA */}
                        <Grid item xs={12} md={7} sx={{ textAlign: { xs: 'center', md: 'left' }, pr: { md: 6, lg: 10 } }}>
                            <motion.h1
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                                className="hero-title"
                                style={{
                                    fontSize: 'clamp(2.2rem, 6vw, 4.2rem)',
                                    fontWeight: 800,
                                    marginBottom: 18,
                                    color: '#f8fafd',
                                    letterSpacing: '-.5px',
                                    lineHeight: 1.13,
                                }}
                            >
                                Effortless Event Planning with AI
                            </motion.h1>
                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                className="hero-description"
                                style={{
                                    fontSize: 'clamp(1.1rem, 2vw, 1.7rem)',
                                    color: '#f8fafd',
                                    maxWidth: 700,
                                    margin: '0 auto',
                                    marginBottom: 32,
                                    fontWeight: 400,
                                    lineHeight: 1.6,
                                }}
                            >
                                All-in-one platform with AI-powered planning, vendor matching, and automated approvals—saving you time and ensuring your event's success.
                            </motion.p>
                            <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' }, width: '100%' }}>
                                <GetEarlyAccessButton setShowLoginCard={setShowLoginCard} />
                            </Box>
                        </Grid>
                        {/* Right: Animation */}
                        <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', pl: { md: 4, lg: 8 } }}>
                            <Box
                                sx={{
                                    width: { xs: '100%', sm: '90%', md: '100%' },
                                    maxWidth: { xs: 340, sm: 480, md: 700 },
                                    minHeight: { xs: 220, sm: 320, md: 400 },
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    borderRadius: 4,
                                    boxShadow: '0 8px 32px rgba(86,55,238,0.08)',
                                    p: { xs: 2, sm: 3, md: 4 },
                                    mx: { xs: 'auto', md: 0 },
                                    mt: { xs: 3, md: 0 },
                                }}
                            >
                                <LandingPageAnimation />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </div>
            <div className="rest-of-content" style={{ display: 'block' }}>
                <Box
                    id="features-section"
                    sx={{
                        py: { xs: 8, md: 20 },
                        backgroundColor: '#ffffff',
                        textAlign: 'center',
                        position: 'relative',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '1px',
                            background: 'linear-gradient(to right, rgba(0,0,0,0), rgba(0,0,0,0.1), rgba(0,0,0,0))',
                        }
                    }}
                >
                    <Container maxWidth="lg" sx={{ padding: { xs: 2, md: 15} }}>
                        <Typography
                            variant="h2"
                            gutterBottom
                            sx={{
                                fontSize: { xs: '2rem', md: '2.5rem' },
                                fontWeight: 600,
                                mb: { xs: 4, md: 5 },
                                textAlign: 'center', // Ensure the title is centered
                            }}
                        >
                            Solutions
                        </Typography>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontSize: { xs: '1rem', md: '1.1rem' },
                                color: '#6B7280',
                                maxWidth: '700px',
                                mx: 'auto',
                                mb: { xs: 5, md: 6 },
                                px: 2
                            }}
                        >
                            Our comprehensive suite of tools designed to streamline your event planning process
                        </Typography>
                        <Grid
                            container
                            spacing={{ xs: 3, sm: 4, md: 5 }}
                            justifyContent="center"
                            alignItems="stretch"
                            sx={{ 
                                maxWidth: '1200px', 
                                mx: 'auto',
                                background: 'rgba(255, 255, 255, 0.7)',
                                borderRadius: '18px',
                                boxShadow: 'none',
                                p: { xs: 1, sm: 2 },
                            }}
                        >
                            {solutionArray.map((feature, index) => (
                                <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={3}
                                    key={index}
                                    sx={{ display: 'flex', justifyContent: 'center', alignItems: 'stretch' }}
                                >
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5, delay: index * 0.1 }}
                                        style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'flex-start',
                                                width: '100%',
                                                maxWidth: 320,
                                                minHeight: 260,
                                                p: { xs: 2, sm: 2.5 },
                                                background: 'none',
                                                borderRadius: 3,
                                                boxShadow: 'none',
                                                mb: 0,
                                            }}
                                        >
                                            <motion.div
                                                whileHover={{ scale: 1.08 }}
                                                whileTap={{ scale: 0.97 }}
                                                transition={{ type: "spring", stiffness: 400, damping: 17 }}
                                                style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                                            >
                                                <Box
                                                    className="feature-icon-container"
                                                    sx={{
                                                        background: '#f3f0ff',
                                                        borderRadius: '16px',
                                                        p: 2,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        mb: 2,
                                                        width: 72,
                                                        height: 72,
                                                        transition: 'all 0.3s ease',
                                                        boxShadow: '0 2px 8px rgba(86,55,238,0.07)',
                                                        '&:hover': {
                                                            background: '#e9e4ff',
                                                            boxShadow: '0 10px 20px rgba(86,55,238,0.10)'
                                                        }
                                                    }}
                                                >
                                                    <feature.icon size={38} color="#5637ee" />
                                                </Box>
                                            </motion.div>
                                            <Typography
                                                className="feature-title"
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: '1.15rem',
                                                    color: '#5637ee',
                                                    mb: 1,
                                                    textAlign: 'center',
                                                }}
                                            >
                                                {feature.title}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ 
                                                    color: '#6B7280', 
                                                    fontSize: '1rem', 
                                                    lineHeight: 1.5,
                                                    maxWidth: '260px',
                                                    mx: 'auto',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                {feature.description}
                                            </Typography>
                                        </Box>
                                    </motion.div>
                                </Grid>
                            ))}
                        </Grid>
                    </Container>
                </Box>

                <Box
                    id="pricing-section"
                    sx={{
                        padding: { xs: 14, md: 25},
                        background: 'linear-gradient(65deg, #5637eec7 0%, #6da7d0ff 68%)',
                        color: 'white',
                        position: 'relative',
                        overflow: 'hidden',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: { xs: 500, md: 600 },
                    }}
                >
                    {/* Background Accent SVG */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: { xs: '-80px', md: '-120px' },
                            left: { xs: '-60px', md: '-100px' },
                            zIndex: 0,
                            opacity: 0.18,
                            pointerEvents: 'none',
                        }}
                    >
                        <svg width="340" height="340" viewBox="0 0 340 340" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="170" cy="170" r="170" fill="#fff" />
                        </svg>
                    </Box>
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.5 }}
                        transition={{ duration: 0.7, ease: 'easeOut' }}
                        style={{ width: '100%', zIndex: 1, display: 'flex', justifyContent: 'center' }}
                    >
                        <Box
                            sx={{
                                maxWidth: { xs: '98%', sm: '90%', md: '540px' },
                                width: { xs: '98%', sm: '80%', md: '540px' },
                                backgroundColor: 'rgba(255, 255, 255, 0.18)',
                                borderRadius: { xs: '20px', md: '24px' },
                                p: { xs: 5, sm: 8, md: 10 },
                                backdropFilter: 'blur(12px)',
                                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.18)',
                                textAlign: 'center',
                                position: 'relative',
                                overflow: 'visible',
                            }}
                        >
                            {/* Beta Badge */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: -24,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    zIndex: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'inline-block',
                                        px: 2.5,
                                        py: 0.5,
                                        background: 'linear-gradient(90deg, #5637ee 60%, #6da7d0 100%)',
                                        color: 'white',
                                        borderRadius: '16px',
                                        fontWeight: 700,
                                        fontSize: '1rem',
                                        letterSpacing: 1,
                                        boxShadow: '0 2px 8px rgba(86,55,238,0.13)',
                                    }}
                                >
                                    Beta
                                </Box>
                            </Box>
                            <Typography
                                variant="h2"
                                sx={{
                                    fontSize: { xs: '2rem', md: '2.5rem' },
                                    fontWeight: 700,
                                    mb: 1.5,
                                    mt: 2.5,
                                    color: '#ffffff',
                                    letterSpacing: '-1px',
                                }}
                            >
                                Beta Access
                            </Typography>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontSize: { xs: '1rem', md: '1.1rem' },
                                    color: '#e0e7ff',
                                    maxWidth: '700px',
                                    mx: 'auto',
                                    mb: { xs: 3, md: 4 },
                                    px: 2,
                                    fontWeight: 400,
                                }}
                            >
                                Get full access to all premium features during our beta phase
                            </Typography>
                            {/* Price and Free Badge */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Typography
                                        variant="h1"
                                        sx={{
                                            fontSize: { xs: '3.2rem', sm: '4rem', md: '5rem' },
                                            fontWeight: 600,
                                            color: '#ffffff',
                                            lineHeight: 1.1,
                                            letterSpacing: '-2px',
                                        }}
                                    >
                                        Free
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'inline-block',
                                            px: 1.5,
                                            py: 0.5,
                                            background: 'linear-gradient(90deg, #6da7d0 0%, #5637ee 100%)',
                                            color: 'white',
                                            borderRadius: '12px',
                                            fontWeight: 700,
                                            fontSize: '1rem',
                                            ml: 1,
                                            boxShadow: '0 2px 8px rgba(86,55,238,0.10)',
                                        }}
                                    >
                                        100% Free
                                    </Box>
                                </Box>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 400,
                                        opacity: 0.9,
                                        fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.15rem' },
                                        color: '#e0e7ff',
                                        mt: 0.5,
                                    }}
                                >
                                    During beta period
                                </Typography>
                            </Box>
                            {/* Features List with Icons */}
                            <Grid container spacing={2} justifyContent="center" alignItems="center" sx={{ mb: { xs: 3, md: 4 }, mt: 1 }}>
                                {[
                                    { icon: <Check sx={{ color: '#5637ee', fontSize: 24 }} />, text: 'Unlimited team members' },
                                    { icon: <BarChart2 size={22} color="#5637ee" />, text: 'Advanced analytics' },
                                    { icon: <Network size={22} color="#5637ee" />, text: 'Knowledge graph' },
                                    { icon: <Users size={22} color="#5637ee" />, text: 'Up to 25 vendors' },
                                    { icon: <Sparkles size={22} color="#5637ee" />, text: 'Priority support' },
                                    { icon: <Wand2 size={22} color="#5637ee" />, text: 'Early access to new features' },
                                ].map((feature, idx) => (
                                    <Grid item xs={12} sm={6} key={idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true, amount: 0.5 }}
                                            transition={{ duration: 0.5, delay: idx * 0.08 }}
                                            style={{ width: '100%' }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    minHeight: 54,
                                                    width: '100%',
                                                    gap: 2,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 38,
                                                        height: 38,
                                                        background: 'rgba(86,55,238,0.08)',
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0,
                                                        mr: 2,
                                                    }}
                                                >
                                                    {feature.icon}
                                                </Box>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        color: '#e0e7ff',
                                                        opacity: 0.8,
                                                        fontWeight: 500,
                                                        fontSize: { xs: '1rem', sm: '1.05rem' },
                                                        textAlign: 'left',
                                                        lineHeight: 1.3,
                                                    }}
                                                >
                                                    {feature.text}
                                                </Typography>
                                            </Box>
                                        </motion.div>
                                    </Grid>
                                ))}
                            </Grid>
                            {/* CTA Button */}
                            <motion.div
                                whileHover={{ scale: 1.06 }}
                                whileTap={{ scale: 0.97 }}
                                transition={{ type: 'spring', stiffness: 400, damping: 18 }}
                                style={{ display: 'inline-block' }}
                            >
                                <Button 
                                    onClick={() => setShowLoginCard(true)}
                                    sx={{
                                        background: 'linear-gradient(90deg, #5637ee 60%, #6da7d0 100%)',
                                        color: 'white',
                                        fontWeight: 700,
                                        px: { xs: 4, md: 6 },
                                        py: { xs: 1.5, md: 2 },
                                        fontSize: { xs: '1.1rem', md: '1.2rem' },
                                        borderRadius: '50px',
                                        textTransform: 'none',
                                        boxShadow: '0 10px 25px rgba(86,55,238,0.13)',
                                        mt: 1,
                                        mb: 0.5,
                                        '&:hover': {
                                            background: 'linear-gradient(90deg, #6da7d0 0%, #5637ee 100%)',
                                            boxShadow: '0 15px 30px rgba(86,55,238,0.18)',
                                        },
                                        transition: 'all 0.3s cubic-bezier(.4,2,.6,1)',
                                    }}
                                >
                                    Request Beta Access
                                    <ArrowUpRight size={20} style={{ marginLeft: '10px' }} />
                                </Button>
                            </motion.div>
                        </Box>
                    </motion.div>
                </Box>

                <Box
                    id="contact-section"
                    sx={{
                        py: { xs: 13, md: 25 },
                        backgroundColor: '#ffffff',
                        textAlign: 'center',
                    }}
                >
                    <Container
                        maxWidth="md"
                        sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                textAlign: 'center',
                        }}
                    >
                        <Typography
                            variant="h2"
                            sx={{
                                fontSize: '2.5rem',
                                fontWeight: 600,
                                mb: 3,
                            }}
                        >
                            Ready to Transform Your Event Planning?
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                mb: 5,
                                color: '#6B7280',
                                fontSize: '1.25rem',
                                fontWeight: 400,
                                lineHeight: 1.6,
                            }}
                        >
                            Join innovative companies already using Happenings to
                            create extraordinary events with less effort.
                        </Typography>
                        <GetEarlyAccessButton setShowLoginCard={setShowLoginCard} />
                    </Container>
                </Box>

                    <Footer />
                </div>
            </div>
            {showLoginCard && (
                <LoginCard
                    onClose={() => setShowLoginCard(false)}
                    onUserLogin={handleLoginSuccess}
                />
            )}
        </>
    )
}

export default LandingPage

